import ActivityForm from './ActivityForm';
import { Button, Drawer, Form, Modal } from 'antd';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/css';
import { useMount } from 'ahooks';
import { getStores } from '@knowledge/common/cache/store';
import { Store, useStoreMap } from 'components/StoreScopes';
import PolicyCard, { PolicyEmpty } from './PolicyForm/PolicyCard';
import Icon from '@ant-design/icons/lib/components/Icon';
import { ReactComponent as TranslateSvg } from 'views/BootGuide/assets/translate.svg';
import { useActivityTypeLabel } from './share';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import posthog from '@leyan/lytics';
import { getActivityTypeHighlightListMap } from '@knowledge/service/api/activity';
import isEqual from 'react-fast-compare';
import { ActivityContext, ActivityContextValue, useActivityModal } from './ActivityContextV2';
import { HelpContextProvider, useHelpContext } from './HelpContext';
import { RightOutlined } from '@ant-design/icons';
import Fold from 'views/Conversation/Sider/Fold';
import { Help, HelpMinimized } from './Help';
import { ScenarioConfigProvider } from './ScenarioConfigContext';

export function useActivityContext() {
  const value = useContext(ActivityContext);

  if (!value) {
    throw new Error(
      'useActivityContext should be used in a context of ActivityContextProvider component',
    );
  }

  return value;
}

function ActivityContextProviderWithout({ children }: { children: ReactNode }) {
  const { t } = useActivityTypeLabel();

  const lang = useSystemLanguageValue();

  const [storeList, setStoreList] = useState<Store[]>([]);

  const [activityTypeHighlightListMap, setActivityTypeHighlightListMap] = useState<
    ActivityContextValue['activityTypeHighlightListMap']
  >({});

  useMount(() => {
    getStores()
      .then(setStoreList)
      .catch(() => {});

    getActivityTypeHighlightListMap()
      .then(setActivityTypeHighlightListMap)
      .catch(() => {});
  });

  const storeMap = useStoreMap({ list: storeList });

  const { visible, show, hide, update, meta } = useActivityModal();

  const {
    activity,
    activities,
    disabled,
    drawerProps,
    scenarioConfigEnable = false,
    onUpdate,
    onUpdatePolicy,
  } = meta || {};

  const isEdit = Boolean(activity?.id);

  const [regenerate, setRegenerate] = useState(0);

  const [_autoTranslate, setAutoTranslate] = useState(true);
  const showTranslate = activity?.language !== lang;
  const autoTranslate = showTranslate && _autoTranslate;

  const [isfold, setIsfold] = useState(false);
  useEffect(() => {
    return () => setIsfold(false);
  }, [activity?.id]);

  const {
    stepState: [, setHelpStep],
    displayState: [helpDisplay],
  } = useHelpContext();

  useEffect(() => {
    if (visible) {
      setHelpStep('促销类型');
    }
  }, [setHelpStep, visible]);

  const [_activityType, setActivityType] = useState<Knowledge.Activity.Type>();
  const activityType = activity?.activityType || _activityType;

  const helpContent = visible && helpDisplay && (
    <Help className={styles.help} activityType={activityType} />
  );

  const helpMinimized = <HelpMinimized />;

  const form = (
    <ActivityForm
      initialValues={activity}
      disabled={disabled}
      storeList={storeList}
      storeMap={storeMap}
      listWithExtra={activities}
      activityTypeHighlightListMap={activityTypeHighlightListMap}
      onActivityTypeChange={setActivityType}
      onUpdate={(nextActivity) => {
        onUpdate?.();
        update(visible, {
          ...meta,
          activity: nextActivity,
        });

        const { activityName, ...activityWithoutName } = activity || {};
        const { activityName: nextActivityName, ...nextActivityWithoutName } = nextActivity;
        if (
          (nextActivity.usageStatus === 'UNUSED' && !regenerate) ||
          !isEqual(activityWithoutName, nextActivityWithoutName)
        ) {
          setRegenerate((pre) => pre + 1);
          setIsfold(true);
        }

        setHelpStep('专属销售方案');
      }}
    >
      {(props) => {
        const withContent = (...content: ReactNode[]) => (
          <Form colon={false}>
            <Fold
              isfold={isfold}
              fixedCount={1}
              style={isfold ? undefined : { overflow: 'unset' }}
              nodeList={[
                <header className={styles.header}>
                  <h4>{t('STEP1 告诉小乐当前的活动信息')}</h4>
                  {isEdit && (
                    <Button
                      type="text"
                      icon={
                        <RightOutlined
                          style={isfold ? undefined : { transform: 'rotateZ(90deg)' }}
                        />
                      }
                      onClick={() => setIsfold(!isfold)}
                    />
                  )}
                </header>,
                ...content,
              ]}
            />
          </Form>
        );

        if (!props.ready) {
          return withContent(
            <div className={styles.content}>
              <section>
                <h5>{t('以下是一些常见的促销类型，请选择本次活动的类型')}</h5>
                <div>{props.type}</div>
              </section>
            </div>,
          );
        }

        const {
          type,
          otherFields,
          formProps: { dirty, isSubmitting, handleSubmit },
        } = props;

        return withContent(
          <div className={styles.content}>
            <section>
              <h5>{t('以下是一些常见的促销类型，请选择本次活动的类型')}</h5>
              <div>{type}</div>
            </section>
            <section>
              <h5>
                {t(
                  '我们需要您填写一些有关促销活动的细节。完成后，我们会在不同时机向买家传达促销信息，帮助您的店铺有效地进行推广和宣传。',
                )}
              </h5>
              <div>{otherFields}</div>
            </section>
          </div>,
          <footer
            className={styles.footer}
            style={isfold ? undefined : { position: 'sticky', bottom: -10 }}
          >
            <Button
              type="primary"
              disabled={
                disabled ||
                (isEdit && !dirty && (activity?.usageStatus === 'USED' || regenerate > 0))
              }
              loading={isSubmitting}
              onClick={() => {
                handleSubmit();
                posthog.capture('$confirmActivityConditions');
              }}
            >
              {t('confirm_activity_conditions')}
            </Button>
          </footer>,
        );
      }}
    </ActivityForm>
  );

  const policyActivity = useMemo(
    () =>
      activity?.id && activity?.activityType
        ? {
            ...activity,
            id: activity.id,
            activityType: activity.activityType,
          }
        : undefined,
    [activity],
  );

  const policyCard = (
    <div>
      <header className={styles.header}>
        <h4>{t('STEP2 查看小乐为店铺生成的专属销售方案')}</h4>
        {showTranslate && (
          <Button
            type={autoTranslate ? 'link' : 'text'}
            size="small"
            onClick={() => setAutoTranslate((pre) => !pre)}
            icon={<Icon component={TranslateSvg} />}
          >
            {t('trade.翻译')}
          </Button>
        )}
      </header>
      {policyActivity && visible ? (
        <PolicyCard
          disabled={disabled}
          autoTranslate={autoTranslate}
          activity={policyActivity}
          regenerate={regenerate}
          onRegenerated={() => {
            onUpdate?.();
            onUpdatePolicy?.();
            setRegenerate(0);
            hide();
          }}
        />
      ) : (
        <PolicyEmpty />
      )}
    </div>
  );

  const drawer = (
    <Drawer
      className={styles.drawer}
      title={
        disabled
          ? lang === 'zh-CN'
            ? '查看活动'
            : t('aide.查看')
          : isEdit
          ? t('adjust_activity')
          : t('create_activity')
      }
      width={1100}
      visible={visible}
      maskClosable={false}
      extra={helpMinimized}
      onClose={() => {
        if (!regenerate) {
          return hide();
        }

        Modal.confirm({
          title: t('confirm_knowledge_policy_contents_tips'),
          onOk: () => {
            hide();
            setRegenerate(0);
          },
        });
      }}
      zIndex={998}
      {...drawerProps}
    >
      <section>
        {form}
        {policyCard}
        {helpContent}
      </section>
    </Drawer>
  );

  return (
    <ActivityContext.Provider
      value={useMemo(
        () => ({
          show,
          storeList,
          storeMap,
          activityTypeHighlightListMap,
        }),
        [show, storeList, storeMap, activityTypeHighlightListMap],
      )}
    >
      <ScenarioConfigProvider enable={scenarioConfigEnable}>
        {visible && drawer}
        {children}
      </ScenarioConfigProvider>
    </ActivityContext.Provider>
  );
}

export function ActivityContextProvider({ children }: { children: ReactNode }) {
  return (
    <HelpContextProvider>
      <ActivityContextProviderWithout>{children}</ActivityContextProviderWithout>
    </HelpContextProvider>
  );
}

const styles = {
  drawer: css`
    .ant-drawer-content-wrapper {
      max-width: calc(100vw - 270px);
    }
    .ant-drawer-body {
      padding: 12px 16px;
      background: #f7f9fa;
      > div {
        flex: 0 0 auto;
        margin-bottom: 16px;
      }
      > section {
        flex: 1 0 0;
        > * {
          margin-bottom: 24px;
        }
        > *:last-child {
          margin-bottom: 0;
        }
      }
    }
    .ant-drawer-extra {
      height: 22px;
    }
  `,
  header: css`
    height: 56px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 24px;
    margin-bottom: 1px;
    background: #fff;
    > h4 {
      margin-bottom: 0;
      font-size: 16px;
    }
  `,
  content: css`
    min-height: 300px;
    padding: 24px;
    background: #fff;
    > section {
      &:not(:first-of-type) {
        margin-top: 16px;
        > div {
          padding-bottom: 1px;
        }
      }
      > h5 {
        font-size: 14px;
        font-weight: 400;
      }
      > div {
        padding: 12px;
        background: #f7f9fa;
        .ant-input-affix-wrapper,
        .ant-input-textarea {
          width: 352px;
          max-width: 100%;
        }
        .ant-form-item-label {
          flex: 0 0 186px;
          white-space: pre-wrap;
        }
      }
    }
  `,
  footer: css`
    height: 56px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 0 16px;
    background: #fff;
    box-shadow: 0px -2px 8px 0px rgba(167, 178, 204, 0.1),
      inset 0px 1px 0px 0px rgba(240, 242, 245, 1);
  `,
  help: css`
    position: fixed;
    top: 40%;
    right: min(1124px, calc(100vw - 244px));
  `,
};
